import { AlgoliaFacets } from '@utils/algolia/knownFacetNames';

import { normalizeFacetKey } from './useRefinementCounts';

type RefinementItem = {
  attribute: string;
  refinements: {
    value: string | number;
    label: string;
    operator?: string;
  }[];
};

export type AppliedFacet = { facetType: string; facetName: string };

const isTrackedAttribute = (attribute: string) =>
  attribute !== AlgoliaFacets.StoreInventory &&
  attribute !== AlgoliaFacets.Collection &&
  !attribute.startsWith('categories');

const FACET_TYPE_OVERRIDES: Record<string, string> = {
  [AlgoliaFacets.ColourVariants]: 'colour',
  [AlgoliaFacets.ReviewScoreFlattened]: 'rating',
};

export const toGa4FacetType = (
  normalizedKey: string,
  priceAttribute: string
): string => {
  if (normalizedKey === priceAttribute) return 'price';
  if (FACET_TYPE_OVERRIDES[normalizedKey]) {
    return FACET_TYPE_OVERRIDES[normalizedKey];
  }
  return normalizedKey
    .replace(/^facets\./, '')
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/\s+/g, '_')
    .toLowerCase();
};

const refinementKey = (
  attribute: string,
  { value, operator = '' }: RefinementItem['refinements'][number]
) => `${attribute}::${operator}${value}`;

export const getRefinementKeys = (items: RefinementItem[]): Set<string> => {
  const keys = new Set<string>();
  for (const item of items) {
    if (!isTrackedAttribute(item.attribute)) continue;
    for (const refinement of item.refinements) {
      keys.add(refinementKey(item.attribute, refinement));
    }
  }
  return keys;
};

const formatPriceRange = (refinements: RefinementItem['refinements']) => {
  const bound = (operator: string) =>
    refinements.find((refinement) => refinement.operator === operator)?.value;
  const min = bound('>=');
  const max = bound('<=');

  if (max === undefined) return `${min}+`;
  return `${min ?? 0}-${max}`;
};

export const getAppliedFacets = (
  prevKeys: Set<string>,
  items: RefinementItem[],
  priceAttribute: string
): AppliedFacet[] => {
  const applied = new Map<string, AppliedFacet>();

  for (const item of items) {
    if (!isTrackedAttribute(item.attribute)) continue;

    const added = item.refinements.find(
      (refinement) => !prevKeys.has(refinementKey(item.attribute, refinement))
    );
    if (!added) continue;

    const key = normalizeFacetKey(item.attribute, priceAttribute);
    if (applied.has(key)) continue;

    applied.set(key, {
      facetType: toGa4FacetType(key, priceAttribute),
      facetName:
        key === priceAttribute
          ? formatPriceRange(item.refinements)
          : added.label,
    });
  }

  return [...applied.values()];
};

export const getActiveFacetSummary = (
  counts: Map<string, number>,
  priceAttribute: string
) => {
  let activeFacetCount = 0;
  const types: string[] = [];

  counts.forEach((count, key) => {
    if (count <= 0) return;
    activeFacetCount += count;
    types.push(toGa4FacetType(key, priceAttribute));
  });

  return {
    activeFacetCount,
    activeFacetTypeCount: types.length,
    activeFacetTypes: types.sort().join('|'),
  };
};
